import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, fmtTime, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/pages/employee/WeeklySummary";
import AdminTimeCard from "@/components/AdminTimeCard";
import { toast } from "sonner";
import { Check, X, Unlock, ArrowLeft, MapPin, FileDown, ClipboardCheck, Pencil, Trash2, Plus, ExternalLink, AlertTriangle, Search, Download } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import DayPunchesMap from "@/components/DayPunchesMap";

export function AdminSubmissions() {
  const [items, setItems] = useState([]);
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") || "");
  const [includeUnfinalized, setIncludeUnfinalized] = useState(true);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    const p = {};
    if (status) p.status = status;
    // Only meaningful when no specific status filter is set
    if (includeUnfinalized && !status) p.include_unfinalized = true;
    const { data } = await api.get("/daily/submissions", { params: p });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, includeUnfinalized]);

  const unfinalizedCount = items.filter((s) => s.status === "unfinalized").length;

  // Unique employee names for the datalist autocomplete — matches the pattern
  // on the Paystubs page. Speeds up name search when a tenant has many
  // employees.
  const employeeSuggestions = useMemo(() => {
    return Array.from(new Set(items.map((p) => p.employee_name).filter(Boolean))).sort();
  }, [items]);

  // Client-side filter: employee name (case-insensitive contains) + optional
  // from/to date range on the submission date. Runs against the already
  // loaded set for instant feedback.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (q && !(s.employee_name || "").toLowerCase().includes(q)) return false;
      if (fromDate && s.date && s.date < fromDate) return false;
      if (toDate && s.date && s.date > toDate) return false;
      return true;
    });
  }, [items, search, fromDate, toDate]);

  // Running totals across the filtered set so the admin can eyeball
  // week/month/employee summaries without exporting.
  //
  // Two parallel buckets instead of a single "estimated pay" cherry-pick:
  //  - hourly  = net_hours × base_rate (per day)
  //  - commission = net_commissionable_revenue × commission_pct (per day)
  // The old single `pay` total summed per-day winners, which produced a
  // number no payroll run will ever match. Payroll decides the final gross
  // at the week level under FLSA; the Projected Weekly Payroll callout
  // below pulls that exact number from `/payroll/preview`.
  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, s) => ({
        hours:      acc.hours      + Number(s?.hours?.net_hours || 0),
        gross:      acc.gross      + Number(s?.production?.gross_invoice_total || 0),
        hourly:     acc.hourly     + Number(s?.pay?.estimated_hourly_pay || 0),
        commission: acc.commission + Number(s?.pay?.estimated_commission_pay || 0),
      }),
      { hours: 0, gross: 0, hourly: 0, commission: 0 },
    );
  }, [filtered]);

  const hasFilters = !!(search || fromDate || toDate);
  const clearFilters = () => { setSearch(""); setFromDate(""); setToDate(""); };

  // -----------------------------------------------------------------
  // Projected Weekly Payroll — shows the FLSA-compliant gross each
  // employee will actually receive on their paystub for any COMPLETE
  // payroll week inside [fromDate, toDate]. We hit `/payroll/preview`
  // for each week, which runs the real `compute_week_payroll` engine
  // (weekly higher-of + FLSA 29 CFR 778.118 half-time OT premium).
  //
  // Partial weeks are intentionally NOT projected — OT and the FLSA
  // premium are weekly concepts, and projecting half a week would
  // mislead more than it helps.
  // -----------------------------------------------------------------
  const [wwStartDay, setWwStartDay] = useState(0); // Monday default
  const [projecting, setProjecting] = useState(false);
  const [projected, setProjected] = useState([]); // [{week_start, week_end, rows:[{employee_id, employee_name, hours, hourly, commission, gross, method_label}]}]

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled && typeof data?.work_week_start_day === "number") {
          setWwStartDay(data.work_week_start_day);
        }
      } catch { /* non-fatal — fall back to Monday */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Enumerate payroll weeks fully contained in [fromDate, toDate].
  // Day-of-week conversion: JS `.getDay()` uses Sun=0..Sat=6, while the
  // backend `work_week_start_day` uses Python Mon=0..Sun=6. Normalize
  // both sides to the Python convention before comparing.
  const completeWeeks = useMemo(() => {
    if (!fromDate || !toDate) return [];
    const start = new Date(`${fromDate}T00:00:00`);
    const end   = new Date(`${toDate}T00:00:00`);
    if (isNaN(start) || isNaN(end) || start > end) return [];
    const pyDay = (d) => (d.getDay() + 6) % 7; // Sun=0..Sat=6 → Mon=0..Sun=6
    const alignedStart = new Date(start);
    while (pyDay(alignedStart) !== wwStartDay) {
      alignedStart.setDate(alignedStart.getDate() + 1);
      if (alignedStart > end) return [];
    }
    const weeks = [];
    let cursor = new Date(alignedStart);
    while (true) {
      const wEnd = new Date(cursor);
      wEnd.setDate(wEnd.getDate() + 6);
      if (wEnd > end) break;
      const iso = (d) => d.toISOString().slice(0, 10);
      weeks.push({ week_start: iso(cursor), week_end: iso(wEnd) });
      cursor.setDate(cursor.getDate() + 7);
    }
    return weeks;
  }, [fromDate, toDate, wwStartDay]);

  // Scope the projection to whatever employees are currently visible in
  // the filtered table (so a name filter narrows the callout too).
  const visibleEmployeeIds = useMemo(() => {
    return Array.from(new Set(filtered.map((s) => s.user_id).filter(Boolean)));
  }, [filtered]);

  useEffect(() => {
    let cancelled = false;
    if (completeWeeks.length === 0 || visibleEmployeeIds.length === 0) {
      setProjected([]);
      return;
    }
    (async () => {
      setProjecting(true);
      try {
        const results = await Promise.all(
          completeWeeks.map((w) =>
            api.post("/payroll/preview", {
              week_start: w.week_start,
              employee_ids: visibleEmployeeIds,
            }).then((r) => ({ week: w, data: r.data })).catch(() => null)
          ),
        );
        if (cancelled) return;
        const out = results.filter(Boolean).map(({ week, data }) => ({
          week_start: data.week_start || week.week_start,
          week_end:   data.week_end   || week.week_end,
          rows: (data.rows || []).map((r) => ({
            employee_id: r.employee_id,
            employee_name: r.employee_name,
            hours: Number(r?.calc?.total_hours || 0),
            regular_hours: Number(r?.calc?.regular_hours || 0),
            overtime_hours: Number(r?.calc?.overtime_hours || 0),
            hourly: Number(r?.calc?.hourly_total || 0),
            commission: Number(r?.calc?.commission_pay || 0),
            flsa_ot_premium: Number(r?.calc?.flsa_ot_premium || 0),
            gross: Number(r?.calc?.gross_pay || 0),
            method_label: r?.calc?.method_used_label || r?.calc?.pay_method_used || "",
          })),
        }));
        setProjected(out);
      } finally {
        if (!cancelled) setProjecting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [completeWeeks, visibleEmployeeIds]);

  // CSV export of the FILTERED view — useful for building an employee-
  // specific report for a custom timeframe (payroll audit, dispute review).
  // Buckets are kept separate (hourly vs commission) so the CSV never
  // implies a per-day winner-pick that payroll will not produce.
  const exportCsv = () => {
    if (filtered.length === 0) { toast.info("Nothing to export — adjust your filters"); return; }
    const rows = [
      ["Date", "Employee", "Status", "Net Hours", "Gross Invoice", "Hourly Bucket", "Commission Bucket", "Pay Method"],
      ...filtered.map((s) => [
        s.date || "",
        s.employee_name || "",
        (s.status || "").replace(/_/g, " "),
        Number(s?.hours?.net_hours || 0).toFixed(2),
        Number(s?.production?.gross_invoice_total || 0).toFixed(2),
        Number(s?.pay?.estimated_hourly_pay || 0).toFixed(2),
        Number(s?.pay?.estimated_commission_pay || 0).toFixed(2),
        s.pay_method || "",
      ]),
    ];
    const csv = rows.map((r) => r.map((cell) => {
      const val = String(cell ?? "");
      return /[",\n]/.test(val) ? `"${val.replace(/"/g, '""')}"` : val;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const emp = search ? `-${search.replace(/[^a-z0-9]+/gi, "_")}` : "";
    const range = (fromDate || toDate) ? `-${fromDate || "start"}_to_${toDate || "end"}` : "";
    a.href = url;
    a.download = `submissions${emp}${range}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="admin-submissions-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Daily Submissions</h1>
          <p className="text-xs text-slate-500 tabular-nums mt-1" data-testid="submissions-count-note">
            Showing <b className="text-slate-900">{filtered.length}</b> of {items.length} submissions
            {hasFilters && <> · <button type="button" onClick={clearFilters} className="text-sky-600 hover:underline" data-testid="submissions-clear-filters-btn">clear filters</button></>}
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="include-unfinalized-toggle-wrapper">
            <Switch
              checked={includeUnfinalized}
              onCheckedChange={setIncludeUnfinalized}
              disabled={!!status}
              data-testid="include-unfinalized-toggle"
            />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Include Unfinalized</span>
          </label>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="status-filter-select"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <Card className="rounded-2xl border-slate-200" data-testid="submissions-filter-panel">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr_1fr_auto] gap-3 items-end">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Employee</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="search"
                  autoComplete="off"
                  list="submissions-employee-list"
                  placeholder="Search by name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="submissions-employee-search"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md hover:bg-slate-100 flex items-center justify-center"
                    data-testid="submissions-employee-search-clear"
                  >
                    <X className="h-3.5 w-3.5 text-slate-500" />
                  </button>
                )}
                <datalist id="submissions-employee-list">
                  {employeeSuggestions.map((n) => <option key={n} value={n} />)}
                </datalist>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-600">From (date)</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1"
                data-testid="submissions-from-date"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-600">To (date)</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1"
                data-testid="submissions-to-date"
              />
            </div>
            <Button
              onClick={exportCsv}
              disabled={filtered.length === 0}
              variant="outline"
              className="h-10 shrink-0"
              data-testid="submissions-export-csv-btn"
              title={filtered.length === 0 ? "Nothing matches your filters" : "Download filtered set as CSV"}
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {unfinalizedCount > 0 && (
        <Card className="rounded-2xl border-amber-300 bg-amber-50" data-testid="unfinalized-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900">
                {unfinalizedCount} unfinalized {unfinalizedCount === 1 ? "day" : "days"}
              </p>
              <p className="text-xs text-amber-800">
                These employees clocked in/out but never tapped <span className="font-semibold">Finalize</span>. Hours still count for payroll — review &amp; lock manually, or generate payroll to auto-lock them.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Employee</th>
                <th className="text-right px-4 py-3 font-semibold">Hours</th>
                <th className="text-right px-4 py-3 font-semibold">Gross Invoice</th>
                <th className="text-right px-4 py-3 font-semibold" title="Hours × base hourly rate for the day. OT premium is applied by payroll at the week level, not per day.">Hourly</th>
                <th className="text-right px-4 py-3 font-semibold" title="Net commissionable revenue × commission %. FLSA half-time OT premium is applied by payroll at the week level.">Commission</th>
                <th className="text-center px-4 py-3 font-semibold">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const isUnfinalized = s.status === "unfinalized" || !s.id;
                const rowKey = s.id || `unf-${s.user_id}-${s.date}`;
                const goTo = isUnfinalized
                  ? `/admin/submissions/day?user=${encodeURIComponent(s.user_id)}&date=${encodeURIComponent(s.date)}`
                  : `/admin/submissions/${s.id}`;
                return (
                  <tr
                    key={rowKey}
                    className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${isUnfinalized ? "bg-amber-50/40" : ""}`}
                    onClick={() => navigate(goTo)}
                    data-testid={isUnfinalized ? `unfinalized-row-${s.user_id}-${s.date}` : `submission-row-${s.id}`}
                  >
                    <td className="px-4 py-3 text-slate-700">{fmtDate(s.date)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {s.employee_name}
                      {s.has_pending_shop_time && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800" title="Pending shop time">Shop</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">{s.hours?.net_hours?.toFixed(2)}h</td>
                    <td className="px-4 py-3 text-right">{fmtMoney(s.production?.gross_invoice_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-sky-700 font-semibold" data-testid={`cell-hourly-${rowKey}`}>{isUnfinalized ? "—" : fmtMoney(s.pay?.estimated_hourly_pay)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-indigo-700 font-semibold" data-testid={`cell-commission-${rowKey}`}>{isUnfinalized ? "—" : fmtMoney(s.pay?.estimated_commission_pay)}</td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-3 text-right text-sky-700 font-semibold">{isUnfinalized ? "Finalize →" : "Review →"}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-500" data-testid="submissions-empty-state">
                    {items.length === 0
                      ? "No submissions found."
                      : "No submissions match your filters. Try a different name or date range."}
                  </td>
                </tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-slate-900 text-white" data-testid="submissions-totals-row">
                <tr>
                  <td className="px-4 py-3 text-xs font-extrabold uppercase tracking-widest" colSpan={2}>
                    Report totals · {filtered.length} submission{filtered.length !== 1 ? "s" : ""}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold" data-testid="submissions-totals-hours">{totals.hours.toFixed(2)}h</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold" data-testid="submissions-totals-gross">{fmtMoney(totals.gross)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-lg font-extrabold text-sky-300" data-testid="submissions-totals-hourly">{fmtMoney(totals.hourly)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-lg font-extrabold text-indigo-300" data-testid="submissions-totals-commission">{fmtMoney(totals.commission)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      {/* Projected Weekly Payroll — real FLSA weekly gross from /payroll/preview */}
      {completeWeeks.length > 0 && visibleEmployeeIds.length > 0 && (
        <Card className="rounded-2xl border-emerald-200 bg-white" data-testid="projected-weekly-payroll">
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-3">
              <ClipboardCheck className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-slate-900">Projected weekly payroll</h3>
                <p className="text-sm text-slate-600 mt-0.5">
                  This is what each employee will actually be paid on their paystub for the complete payroll week(s) inside your filter. It uses the real payroll engine — weekly higher-of + FLSA half-time OT premium (29&nbsp;CFR&nbsp;778.118). The Hourly and Commission columns in the table above are the two buckets that feed this calculation.
                </p>
              </div>
            </div>
            {projecting && projected.length === 0 ? (
              <div className="text-sm text-slate-500">Loading projection…</div>
            ) : projected.length === 0 ? (
              <div className="text-sm text-slate-500">No projection available for this range.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Week</th>
                      <th className="text-left px-3 py-2 font-semibold">Employee</th>
                      <th className="text-right px-3 py-2 font-semibold">Reg / OT Hrs</th>
                      <th className="text-right px-3 py-2 font-semibold">Hourly</th>
                      <th className="text-right px-3 py-2 font-semibold">Commission</th>
                      <th className="text-right px-3 py-2 font-semibold">FLSA OT prem</th>
                      <th className="text-left px-3 py-2 font-semibold">Method</th>
                      <th className="text-right px-3 py-2 font-semibold">Projected gross</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projected.flatMap((wk) =>
                      wk.rows.map((r) => (
                        <tr key={`${wk.week_start}-${r.employee_id}`} className="border-b border-slate-100" data-testid={`projected-row-${r.employee_id}-${wk.week_start}`}>
                          <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                            {fmtDate(wk.week_start)} → {fmtDate(wk.week_end)}
                          </td>
                          <td className="px-3 py-2 font-semibold text-slate-900">{r.employee_name}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                            {r.regular_hours.toFixed(1)} / {r.overtime_hours.toFixed(1)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-sky-700">{fmtMoney(r.hourly)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-indigo-700">{fmtMoney(r.commission)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.flsa_ot_premium > 0 ? fmtMoney(r.flsa_ot_premium) : "—"}</td>
                          <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{r.method_label}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-lg font-extrabold text-emerald-700" data-testid={`projected-gross-${r.employee_id}-${wk.week_start}`}>
                            {fmtMoney(r.gross)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function AdminSubmissionDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const dayUser = searchParams.get("user");
  const dayDate = searchParams.get("date");
  // Day mode is active when we mount via /admin/submissions/day?user=...&date=...
  // (no :id param) OR /admin/submissions/:id where id is literally "day".
  const isDayMode = (!id || id === "day") && !!dayUser && !!dayDate;
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [editJob, setEditJob] = useState(null); // job currently being edited
  const [jobForm, setJobForm] = useState(null);
  const [jobBusy, setJobBusy] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    const url = isDayMode
      ? `/admin/day-detail?user_id=${encodeURIComponent(dayUser)}&date=${encodeURIComponent(dayDate)}`
      : `/daily/submissions/${id}`;
    const [d, cf] = await Promise.all([
      api.get(url),
      api.get("/settings/custom-job-fields").catch(() => ({ data: { fields: [] } })),
    ]);
    setData(d.data);
    setNotes(d.data.submission?.admin_notes || "");
    setCustomFields(cf.data?.fields || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, dayUser, dayDate]);

  const openJobEdit = (job) => {
    setEditJob(job);
    setJobForm({
      customer_name: job.customer_name || "",
      job_description: job.job_description || "",
      invoice_number: job.invoice_number || "",
      invoice_amount: String(job.invoice_amount ?? ""),
      total_hours: String(job.total_hours ?? ""),
      allowable_deduction: String(job.allowable_deduction ?? 0),
      final_walk_around: job.final_walk_around,
      final_payment_method: job.final_payment_method || "",
      check_number: job.check_number || "",
      notes: job.notes || "",
    });
  };

  const openJobAdd = () => {
    // Sentinel object — `__isNew` tells saveJobEdit to POST instead of PUT,
    // and carries the target user_id + date so the backend attributes the job
    // to the tech (not the admin) and to the correct historical day.
    setEditJob({ __isNew: true, user_id: data?.submission?.user_id, date: data?.submission?.date });
    setJobForm({
      customer_name: "",
      job_description: "",
      invoice_number: "",
      invoice_amount: "",
      total_hours: "",
      allowable_deduction: "0",
      final_walk_around: null,
      final_payment_method: "",
      check_number: "",
      notes: "",
    });
  };

  const saveJobEdit = async () => {
    if (!editJob || !jobForm) return;
    if (!jobForm.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (jobForm.final_payment_method === "check" && !(jobForm.check_number || "").trim()) {
      toast.error("Check number is required when payment method is 'check'.");
      return;
    }
    const isNew = editJob.__isNew === true;
    if (isNew) {
      if (!(Number(jobForm.invoice_amount) > 0)) {
        toast.error("Invoice amount must be greater than $0.");
        return;
      }
      if (!(Number(jobForm.total_hours) > 0)) {
        toast.error("Total hours must be greater than 0.");
        return;
      }
    }
    setJobBusy(true);
    try {
      const payload = {
        customer_name: jobForm.customer_name.trim(),
        job_description: jobForm.job_description || "",
        invoice_number: jobForm.invoice_number || "",
        invoice_amount: Number(jobForm.invoice_amount) || 0,
        total_hours: Number(jobForm.total_hours) || 0,
        allowable_deduction: Number(jobForm.allowable_deduction) || 0,
        final_walk_around: jobForm.final_walk_around,
        final_payment_method: jobForm.final_payment_method || null,
        check_number: jobForm.final_payment_method === "check" ? (jobForm.check_number || "").trim() : "",
        notes: jobForm.notes || "",
      };
      if (isNew) {
        payload.user_id = editJob.user_id;
        payload.date = editJob.date;
        await api.post("/jobs", payload);
        toast.success("Job added");
      } else {
        await api.put(`/jobs/${editJob.id}`, payload);
        toast.success("Job updated");
      }
      setEditJob(null);
      setJobForm(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setJobBusy(false);
    }
  };

  const deleteJob = async (job) => {
    if (!confirm(`Delete job for "${job.customer_name}"? This will recompute pay for the day.`)) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Job deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!data) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  const { submission: s, time_entry: t, lunch_entry: l, job_entries: jobs, shop_time: shop, truck_inspection: insp, inspection_signature: inspSig, gps_logs: gps, signature, employee } = data;

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/reports/submission-pdf/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `submission_${s.date}_${employee?.last_name || "user"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/daily/submissions/${id}/approve`, { admin_notes: notes });
      toast.success("Approved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!notes) { toast.error("Provide rejection notes"); return; }
    setBusy(true);
    try {
      await api.post(`/daily/submissions/${id}/reject`, { admin_notes: notes });
      toast.success("Rejected");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const unlock = async () => {
    if (!confirm("Delete this submission entirely so the tech restarts from scratch?\n\nThis is destructive — prefer 'Reopen for edits' if you just want to let them fix a few things.")) return;
    setBusy(true);
    try {
      await api.post(`/daily/submissions/${id}/unlock`);
      toast.success("Submission deleted");
      navigate("/admin/submissions");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reopen = async () => {
    // Optional reason — the admin can leave it blank and just re-open.
    const reason = window.prompt(
      "Reopen this day so the tech can edit again?\n\nOptional — leave blank for none.\nAdd a brief reason (e.g. 'missed a job on the 24th') so the tech knows what to fix:",
      ""
    );
    if (reason === null) return; // user hit cancel
    setBusy(true);
    try {
      const res = await api.post(`/daily/submissions/${id}/reopen`, { reason: reason.trim() });
      toast.success("Day reopened — tech can edit and it'll come back to your review queue.");
      await load();
      // eslint-disable-next-line no-unused-vars
      const _ = res;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Force-finalize a day the employee never submitted. Creates the daily
  // submission on the employee's behalf using the current punches/jobs and
  // (by default) approves it in one shot. Only works when we're in day-
  // detail mode (isDayMode) since we need user_id + date.
  const adminFinalize = async (autoApprove) => {
    if (!isDayMode) return;
    const msg = autoApprove
      ? "Force-finalize this day AND approve it on behalf of the employee?\n\nThis will create the submission using the current punches/jobs and mark it approved. Use this when the employee left the day un-finalized."
      : "Force-finalize this day (pending approval)?\n\nThis creates the submission — you can approve/reject it afterward.";
    if (!confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/submissions/force-finalize", {
        user_id: dayUser,
        date: dayDate,
        // Leave pay_method unset — backend defaults to the employee's
        // configured `default_pay_method` (higher_of / hourly / commission /
        // hourly_plus_commission). NEVER pass a delivery method like
        // "direct_deposit" here; that leaked into paystub math historically
        // and caused hourly + commission to be ADDED instead of the higher
        // of the two.
        admin_notes: notes || undefined,
        auto_approve: !!autoApprove,
      });
      toast.success(autoApprove ? "Day force-finalized & approved" : "Day force-finalized — pending approval");
      // Navigate to the newly-created submission so the admin sees the
      // proper approve/reject view going forward.
      navigate(`/admin/submissions/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const approveShop = async (sid) => {
    setBusy(true);
    try {
      await api.post(`/shop-time/${sid}/approve`);
      toast.success("Shop time approved");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const rejectShop = async (sid) => {
    const reason = prompt("Reason for rejection (required):");
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/shop-time/${sid}/reject`, { admin_notes: reason.trim() });
      toast.success("Shop time rejected");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-submission-detail">
      <Button variant="ghost" onClick={() => navigate(-1)} className="px-0"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>

      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{fmtDate(s.date)}</p>
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">{employee?.first_name} {employee?.last_name}</h1>
          <p className="text-sm text-slate-500">{employee?.email}</p>
        </div>
        <div className="flex items-center gap-3">
          {s.id && (
            <Button variant="outline" onClick={downloadPdf} data-testid="download-submission-pdf-btn">
              <FileDown className="h-4 w-4 mr-1" /> Download PDF
            </Button>
          )}
          <StatusBadge status={s.status} />
        </div>
      </header>

      {Array.isArray(s.employee_edit_requests) && s.employee_edit_requests.length > 0 && (
        <Card className="rounded-2xl border-amber-300 bg-amber-50" data-testid="employee-edit-requests-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold font-display text-amber-900">Employee edit request{s.employee_edit_requests.length > 1 ? "s" : ""}</h3>
              <span className="text-[10px] font-bold uppercase tracking-widest text-amber-900 bg-amber-200 px-2 py-0.5 rounded-full">Needs review</span>
            </div>
            <ul className="space-y-2">
              {s.employee_edit_requests.map((r, idx) => (
                <li key={r.id || idx} className="rounded-xl bg-white border border-amber-200 p-3" data-testid={`edit-request-${idx}`}>
                  <p className="text-sm text-slate-900"><span className="font-semibold">{r.requested_by_name || "Employee"}</span> changed <span className="font-semibold">{r.changes || "time"}</span></p>
                  <p className="text-xs text-slate-700 mt-1">Reason: <span className="italic">"{r.reason}"</span></p>
                  <p className="text-[11px] text-slate-500 mt-1">{r.requested_at ? new Date(r.requested_at).toLocaleString() : ""} · prior status: {r.prior_status || "—"}</p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <AdminTimeCard
          submission={s}
          timeEntry={t}
          lunchEntry={l}
          onRefresh={load}
        />
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-5 space-y-2">
            <h3 className="font-bold font-display mb-2">Pay</h3>
            <Row label="Pay Method" value={s.pay_method} />
            <Row label="Hourly Pay" value={fmtMoney(s.pay?.estimated_hourly_pay)} />
            <Row label="Commission" value={fmtMoney(s.pay?.estimated_commission_pay)} />
            {Number(s.shop_pay || 0) > 0 && <Row label="Shop Pay" value={fmtMoney(s.shop_pay)} />}
            <Row label="Final" value={fmtMoney(s.pay?.estimated_final_pay)} bold />
          </CardContent>
        </Card>
      </div>

      {insp && (
        <Card className="rounded-2xl border-slate-200" data-testid="submission-inspection-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold font-display flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4" /> Truck Pre-Inspection
              </h3>
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${insp.all_checked ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}`}>
                {insp.all_checked ? "All checked" : "Has notes"}
              </span>
            </div>
            <div className="space-y-1.5">
              {(insp.items || []).map((it, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm" data-testid={`insp-item-${idx}`}>
                  <span className={`inline-block w-4 h-4 mt-0.5 rounded border ${it.checked ? "bg-green-600 border-green-600" : "bg-white border-slate-300"} flex-shrink-0 text-white text-[10px] text-center leading-4 font-bold`}>
                    {it.checked ? "✓" : ""}
                  </span>
                  <div className="flex-1">
                    <span className={`${it.checked ? "text-slate-900" : "text-slate-700"}`}>{it.name}</span>
                    {it.note && <p className="text-xs text-slate-500 italic">↳ {it.note}</p>}
                  </div>
                </div>
              ))}
            </div>
            {insp.notes && (
              <div className="mt-3 text-xs text-slate-600 border-t border-slate-100 pt-2">
                <span className="font-semibold">Notes:</span> {insp.notes}
              </div>
            )}
            {inspSig && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <p className="text-xs text-slate-500 mb-1">Lead Technician Signature</p>
                <img src={inspSig.signature_data} alt="signature" className="bg-white border border-slate-200 rounded-xl max-h-24" />
                <p className="text-xs text-slate-500 mt-1">{fmtTime(inspSig.timestamp)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {(shop?.length > 0 || s.has_pending_shop_time) && (
        <Card className="rounded-2xl border-slate-200" data-testid="submission-shop-time-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold font-display">Shop / Other Time ({shop?.length || 0})</h3>
              <div className="text-xs text-slate-500">
                <span className="text-green-700 font-semibold">{Number(s.shop_hours_approved || 0).toFixed(2)}h approved</span>
                {Number(s.shop_hours_pending || 0) > 0 && (
                  <span className="text-amber-600 font-semibold ml-2">{Number(s.shop_hours_pending).toFixed(2)}h pending</span>
                )}
              </div>
            </div>
            {s.has_pending_shop_time && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 mb-3">
                Pending shop time on this day. Approve or reject individual entries below — they will be added to the employee&apos;s pay automatically.
              </div>
            )}
            <div className="space-y-2">
              {(shop || []).map((st) => (
                <div key={st.id} className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`detail-shop-row-${st.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900">{st.category} <span className="font-normal text-slate-500">· {Number(st.hours).toFixed(2)}h</span></p>
                    {st.notes && <p className="text-xs text-slate-500">{st.notes}</p>}
                    {st.status === "rejected" && st.admin_notes && (
                      <p className="text-xs text-red-600 mt-1">Rejected: {st.admin_notes}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      st.status === "approved" ? "bg-green-100 text-green-700"
                      : st.status === "rejected" ? "bg-red-100 text-red-700"
                      : "bg-amber-100 text-amber-800"
                    }`}>{st.status}</span>
                    {st.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => approveShop(st.id)} disabled={busy} className="bg-green-600 hover:bg-green-700 h-7 px-2" data-testid={`detail-approve-shop-${st.id}`}>
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => rejectShop(st.id)} disabled={busy} className="h-7 px-2" data-testid={`detail-reject-shop-${st.id}`}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {(!shop || shop.length === 0) && <p className="text-sm text-slate-500">No shop time entries.</p>}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold font-display">Jobs ({jobs.length})</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={openJobAdd}
              disabled={jobs.length >= 5}
              className="h-8"
              data-testid="admin-add-job-btn"
              title={jobs.length >= 5 ? "5 jobs/day cap reached" : `Add a job for ${employee?.first_name || "this tech"} on ${fmtDate(s.date)}`}
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add job
            </Button>
          </div>
          <div className="space-y-2">
            {jobs.length === 0 && (
              <p className="text-sm text-slate-500 italic py-2" data-testid="admin-jobs-empty">
                No jobs on this day. Tap <b>Add job</b> to fill one in on the tech&apos;s behalf.
              </p>
            )}
            {jobs.map((j) => (
              <div key={j.id} className="py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900">{j.customer_name}</p>
                    <p className="text-xs text-slate-500">{j.job_description || j.invoice_number || "—"}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {typeof j.final_walk_around === "boolean" && (
                        <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${j.final_walk_around ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          Walk-around: {j.final_walk_around ? "Yes" : "No"}
                        </span>
                      )}
                      {j.final_payment_method && (
                        <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-700">
                          {{cash: "Cash", check: "Check", send_invoice: "Send Invoice"}[j.final_payment_method] || j.final_payment_method}
                          {j.final_payment_method === "check" && j.check_number && (
                            <span className="ml-1 font-mono normal-case tracking-normal">#{j.check_number}</span>
                          )}
                        </span>
                      )}
                      {customFields.map((f) => {
                        const v = (j.custom_field_values || {})[f.id];
                        if (v === undefined || v === null || v === "" || v === false) return null;
                        let displayVal;
                        if (f.type === "checkbox") displayVal = "Yes";
                        else if (f.type === "radio_yes_no") displayVal = String(v).toUpperCase();
                        else displayVal = String(v);
                        const isPositive = (f.type === "radio_yes_no" && v === "yes") || (f.type === "checkbox" && v === true);
                        const isNegative = (f.type === "radio_yes_no" && v === "no");
                        return (
                          <span
                            key={f.id}
                            className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isPositive ? "bg-green-100 text-green-700" : isNegative ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}
                            data-testid={`job-cfield-${j.id}-${f.id}`}
                          >
                            {f.label}: {displayVal}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-green-700">{fmtMoney(j.invoice_amount)}</p>
                    {Number(j.allowable_deduction) > 0 && <p className="text-xs text-red-600">-{fmtMoney(j.allowable_deduction)}</p>}
                    <div className="flex items-center justify-end gap-1 mt-1">
                      <Button variant="ghost" size="sm" onClick={() => openJobEdit(j)} className="h-7 px-2 text-slate-600 hover:text-sky-700" data-testid={`edit-job-${j.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteJob(j)} className="h-7 px-2 text-slate-600 hover:text-red-700" data-testid={`delete-job-${j.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
                {(j.photo_paths || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid={`admin-job-photos-${j.id}`}>
                    {j.photo_paths.map((p, idx) => (
                      <a
                        key={p}
                        href={`${api.defaults.baseURL}/files/${p}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={`${api.defaults.baseURL}/files/${p}`}
                          alt={`${j.customer_name} photo ${idx + 1}`}
                          className="h-16 w-16 object-cover rounded-md border border-slate-200 hover:border-sky-400 transition"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {jobs.length === 0 && <p className="text-sm text-slate-500 py-2">No job entries.</p>}
          </div>
        </CardContent>
      </Card>

      {gps && gps.length > 0 && (
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-bold font-display mb-3 flex items-center gap-2"><MapPin className="h-4 w-4" /> GPS Log</h3>
            <DayPunchesMap points={gps} />
            <div className="mt-3 space-y-1 text-xs font-mono">
              {gps.map((g, idx) => {
                const hasCoords = typeof g.lat === "number" && typeof g.lng === "number";
                const coordEl = hasCoords ? (
                  <a
                    href={`https://www.google.com/maps?q=${g.lat},${g.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-700 hover:text-sky-900 hover:underline inline-flex items-center gap-1"
                    title="Open in Google Maps"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`gps-coord-link-${g.id || idx}`}
                  >
                    {g.lat.toFixed(5)}, {g.lng.toFixed(5)}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span>—</span>
                );
                return (
                  <div key={g.id || idx} className="flex justify-between items-center gap-2 text-slate-600">
                    <span className="capitalize flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-bold">{idx + 1}</span>
                      {g.action?.replace(/_/g, " ") || "punch"}
                    </span>
                    {coordEl}
                    <span>{fmtTime(g.timestamp)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {signature && (
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-bold font-display mb-3">Employee Signature</h3>
            <img src={signature.signature_data} alt="signature" className="bg-white border border-slate-200 rounded-xl max-h-32" />
            <p className="text-xs text-slate-500 mt-2">{fmtTime(signature.timestamp)}</p>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 space-y-3">
          <h3 className="font-bold font-display">Admin Decision</h3>
          {s.status === "no_submission" ? (
            <div className="space-y-3" data-testid="admin-no-submission-actions">
              <p className="text-sm text-slate-600">
                The employee never finalized this day. Their punches, lunch, and jobs above are the source of truth — edit them if needed, then <b>Force-finalize</b> the day on their behalf. This creates the submission and locks the pay math.
              </p>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Admin notes (optional — will be attached to the submission)"
                rows={2}
                data-testid="admin-force-finalize-notes"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => adminFinalize(true)}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="admin-force-finalize-approve-btn"
                >
                  <Check className="h-4 w-4 mr-1" /> Force-finalize &amp; Approve
                </Button>
                <Button
                  onClick={() => adminFinalize(false)}
                  disabled={busy}
                  variant="outline"
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                  data-testid="admin-force-finalize-pending-btn"
                >
                  <ClipboardCheck className="h-4 w-4 mr-1" /> Force-finalize (pending approval)
                </Button>
              </div>
              <p className="text-[11px] text-slate-500">
                <b>Missing clock-in/out?</b> Add the punches above first — the force-finalize will fail until the day has a complete clock-in and clock-out.
              </p>
            </div>
          ) : (
            <>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (required for rejection)" rows={2} data-testid="admin-notes-input" />
              <div className="flex flex-wrap gap-2">
                <Button onClick={approve} disabled={busy || s.status === "approved"} className="bg-green-600 hover:bg-green-700" data-testid="approve-btn">
                  <Check className="h-4 w-4 mr-1" /> Approve
                </Button>
                <Button onClick={reject} disabled={busy || s.status === "rejected"} variant="destructive" data-testid="reject-btn">
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
                {(s.status === "approved" || s.status === "rejected") && s.status !== "locked" && (
                  <Button onClick={reopen} disabled={busy} variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="reopen-btn"
                    title="Send this day back to the tech so they can fix the punches or jobs. Non-destructive — data is preserved.">
                    <Unlock className="h-4 w-4 mr-1" /> Reopen for edits
                  </Button>
                )}
                <Button onClick={unlock} disabled={busy} variant="ghost" className="text-slate-500" data-testid="unlock-btn"
                  title="Destructive — deletes the submission so the tech starts over from scratch. Prefer 'Reopen for edits' unless the day is genuinely wrong.">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete submission
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Job Edit / Add Dialog (admin-only) */}
      <Dialog open={!!editJob} onOpenChange={(o) => { if (!o) { setEditJob(null); setJobForm(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-edit-job-dialog">
          <DialogHeader>
            <DialogTitle>
              {editJob?.__isNew
                ? `Add Job — ${employee?.first_name || "tech"} · ${data?.submission ? fmtDate(data.submission.date) : ""}`
                : `Edit Job — ${editJob?.customer_name}`}
            </DialogTitle>
          </DialogHeader>
          {jobForm && (
            <div className="space-y-3">
              <JobField label="Customer Name *">
                <Input value={jobForm.customer_name} onChange={(e) => setJobForm({ ...jobForm, customer_name: e.target.value })} data-testid="edit-job-customer" />
              </JobField>
              <JobField label="Description">
                <Input value={jobForm.job_description} onChange={(e) => setJobForm({ ...jobForm, job_description: e.target.value })} data-testid="edit-job-desc" />
              </JobField>
              <div className="grid grid-cols-2 gap-2">
                <JobField label="Invoice #">
                  <Input value={jobForm.invoice_number} onChange={(e) => setJobForm({ ...jobForm, invoice_number: e.target.value })} data-testid="edit-job-invoice-num" />
                </JobField>
                <JobField label="Invoice Amount ($)">
                  <Input type="number" step="0.01" value={jobForm.invoice_amount} onChange={(e) => setJobForm({ ...jobForm, invoice_amount: e.target.value })} data-testid="edit-job-amount" />
                </JobField>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <JobField label="Total Hours">
                  <Input type="number" step="0.25" value={jobForm.total_hours} onChange={(e) => setJobForm({ ...jobForm, total_hours: e.target.value })} data-testid="edit-job-hours" />
                </JobField>
                <JobField label="Allowable Deduction ($)">
                  <Input type="number" step="0.01" value={jobForm.allowable_deduction} onChange={(e) => setJobForm({ ...jobForm, allowable_deduction: e.target.value })} data-testid="edit-job-deduction" />
                </JobField>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <JobField label="Final Walk-Around">
                  <Select
                    value={jobForm.final_walk_around === true ? "yes" : jobForm.final_walk_around === false ? "no" : "unset"}
                    onValueChange={(v) => setJobForm({ ...jobForm, final_walk_around: v === "yes" ? true : v === "no" ? false : null })}
                  >
                    <SelectTrigger data-testid="edit-job-walkaround"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">Not set</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </JobField>
                <JobField label="Payment Method">
                  <Select
                    value={jobForm.final_payment_method || "unset"}
                    onValueChange={(v) => setJobForm({
                      ...jobForm,
                      final_payment_method: v === "unset" ? "" : v,
                      // Clear check number when switching away from check
                      check_number: v === "check" ? jobForm.check_number : "",
                    })}
                  >
                    <SelectTrigger data-testid="edit-job-payment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">Not set</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="send_invoice">Send Invoice</SelectItem>
                    </SelectContent>
                  </Select>
                </JobField>
              </div>
              {jobForm.final_payment_method === "check" && (
                <JobField label="Check # *">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={jobForm.check_number || ""}
                    onChange={(e) => setJobForm({ ...jobForm, check_number: e.target.value })}
                    placeholder="e.g. 1042"
                    data-testid="edit-job-check-number"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Required — the exact number printed on the check.</p>
                </JobField>
              )}
              <JobField label="Notes">
                <Textarea value={jobForm.notes} onChange={(e) => setJobForm({ ...jobForm, notes: e.target.value })} rows={2} data-testid="edit-job-notes" />
              </JobField>
              <p className="text-xs text-slate-500 italic">Your edit is logged in the audit trail. Pay/hours will recompute on save.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setEditJob(null); setJobForm(null); }}>Cancel</Button>
            <Button onClick={saveJobEdit} disabled={jobBusy} className="bg-sky-600 hover:bg-sky-700" data-testid="save-job-edit-btn">
              {jobBusy ? "Saving…" : (editJob?.__isNew ? "Add job" : "Save changes")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function JobField({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={bold ? "font-bold text-slate-900" : "font-medium text-slate-900"}>{value}</span>
    </div>
  );
}
